import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import {
  apiService,
  TaskCreatePayload,
  TaskItem,
  TaskStatus,
  TaskUpdatePayload,
} from '../services/api.service';
import { useAuth } from './auth.context';

/** Псевдопроект для задач без project — отдельная группа на доске. */
export const NO_PROJECT = '__no_project__';

export type TaskScope = 'all' | 'mine';

interface TasksContextType {
  tasks: TaskItem[];
  /** Значения поля project из задач (GET /api/tasks/projects/list) + NO_PROJECT при наличии задач без проекта. */
  projects: string[];
  loading: boolean;
  error: string | null;
  /** «Все» — задачи, которыми владею; «Мои» — mine=true (я исполнитель или создатель). */
  scope: TaskScope;
  setScope: (scope: TaskScope) => void;
  refresh: () => Promise<void>;
  createTask: (payload: TaskCreatePayload) => Promise<TaskItem>;
  updateTask: (id: number, patch: TaskUpdatePayload) => Promise<TaskItem>;
  deleteTask: (id: number) => Promise<void>;
  /** Перенос между колонками: оптимистичное обновление + PATCH /move (статус и позиция). */
  moveTask: (id: number, newStatus: TaskStatus, newPosition: number) => Promise<void>;
  /** Переупорядочивание внутри колонки: оптимистично + PATCH /reorder. */
  reorderColumn: (orderedIds: number[]) => Promise<void>;
}

const TasksContext = createContext<TasksContextType | undefined>(undefined);

function projectKey(task: TaskItem): string {
  return task.project || NO_PROJECT;
}

/** Пересчитывает position внутри группы (project+status) — как это делает бэкенд при move. */
function renumberGroup(tasks: TaskItem[], project: string, status: TaskStatus): TaskItem[] {
  let index = 0;
  return tasks.map(task => {
    if (projectKey(task) !== project || task.status !== status) return task;
    return { ...task, position: index++ };
  });
}

export const TasksProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthed } = useAuth();
  const [tasks, setTasks] = useState<TaskItem[]>([]);
  const [apiProjects, setApiProjects] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [scope, setScope] = useState<TaskScope>('all');

  const refresh = useCallback(async () => {
    if (!isAuthed) {
      setTasks([]);
      setApiProjects([]);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const [loadedTasks, loadedProjects] = await Promise.all([
        apiService.getAllTasks(scope === 'mine' ? { mine: true } : {}),
        apiService.getTaskProjects().catch(() => [] as string[]),
      ]);
      setTasks(loadedTasks);
      setApiProjects(loadedProjects);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Не удалось загрузить задачи');
      setTasks([]);
    } finally {
      setLoading(false);
    }
  }, [isAuthed, scope]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const projects = useMemo(() => {
    const set = new Set(apiProjects.filter(Boolean));
    tasks.forEach(task => {
      if (task.project) set.add(task.project);
    });
    const list = Array.from(set).sort((a, b) => a.localeCompare(b, 'ru'));
    if (tasks.some(task => !task.project)) list.push(NO_PROJECT);
    return list;
  }, [apiProjects, tasks]);

  const createTask = useCallback(async (payload: TaskCreatePayload) => {
    const created = await apiService.createTask(payload);
    setTasks(prev => [...prev, created]);
    if (created.project) {
      setApiProjects(prev => (prev.includes(created.project!) ? prev : [...prev, created.project!]));
    }
    return created;
  }, []);

  const updateTask = useCallback(async (id: number, patch: TaskUpdatePayload) => {
    const updated = await apiService.updateTask(id, patch);
    setTasks(prev => prev.map(task => (task.id === id ? updated : task)));
    return updated;
  }, []);

  const deleteTask = useCallback(async (id: number) => {
    await apiService.deleteTask(id);
    setTasks(prev => prev.filter(task => task.id !== id));
  }, []);

  const moveTask = useCallback(async (id: number, newStatus: TaskStatus, newPosition: number) => {
    let snapshot: TaskItem[] = [];
    setTasks(prev => {
      snapshot = prev;
      const moved = prev.find(task => task.id === id);
      if (!moved) return prev;
      const project = projectKey(moved);
      const oldStatus = moved.status;

      // Собираем целевую колонку без перемещаемой задачи и вставляем её на новую позицию.
      const targetColumn = prev
        .filter(task => task.id !== id && projectKey(task) === project && task.status === newStatus)
        .sort((a, b) => a.position - b.position);
      const insertAt = Math.max(0, Math.min(newPosition, targetColumn.length));
      const targetIds = targetColumn.map(task => task.id);
      targetIds.splice(insertAt, 0, id);
      const targetOrder = new Map(targetIds.map((taskId, index) => [taskId, index]));

      let next = prev.map(task => {
        if (task.id === id) {
          return { ...task, status: newStatus, position: targetOrder.get(id) ?? insertAt };
        }
        if (projectKey(task) === project && task.status === newStatus && targetOrder.has(task.id)) {
          return { ...task, position: targetOrder.get(task.id)! };
        }
        return task;
      });
      if (oldStatus !== newStatus) {
        next = renumberGroup(next, project, oldStatus);
      }
      return next;
    });

    try {
      await apiService.moveTask(id, newStatus, newPosition);
    } catch (e) {
      setTasks(snapshot);
      throw e;
    }
  }, []);

  const reorderColumn = useCallback(async (orderedIds: number[]) => {
    if (!orderedIds.length) return;
    let snapshot: TaskItem[] = [];
    setTasks(prev => {
      snapshot = prev;
      const order = new Map(orderedIds.map((taskId, index) => [taskId, index]));
      return prev.map(task => (order.has(task.id) ? { ...task, position: order.get(task.id)! } : task));
    });

    try {
      await apiService.reorderTasks(orderedIds);
    } catch (e) {
      setTasks(snapshot);
      throw e;
    }
  }, []);

  const value = useMemo(
    () => ({
      tasks,
      projects,
      loading,
      error,
      scope,
      setScope,
      refresh,
      createTask,
      updateTask,
      deleteTask,
      moveTask,
      reorderColumn,
    }),
    [tasks, projects, loading, error, scope, refresh, createTask, updateTask, deleteTask, moveTask, reorderColumn],
  );

  return <TasksContext.Provider value={value}>{children}</TasksContext.Provider>;
};

export const useTasks = () => {
  const ctx = useContext(TasksContext);
  if (!ctx) throw new Error('useTasks must be used within TasksProvider');
  return ctx;
};
